import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { trackLink } from '@fs/zion-analytics'
import { ContentAdd, ContentCheck } from '@fs/zion-icon'
import { useUser } from '@fs/zion-user'
import { Skeleton, useOverlay } from '@fs/zion-ui'
import { useLoggedOutOverlay } from '../../providers/LoggedOutOverlayProvider'
import { useTimeTravel, useFlagsContext, useConferenceContext } from '../../providers'
import { FLAGS } from '../../config/flags'
import ChewableButton from './ChewableButton'
import { PlaylistManagementOverlay } from '../../components/playlists/components/PlaylistManagementOverlay/PlaylistManagementOverlay'
import { isContentAvailable } from '../helpers'
import { usePlaylistsByItems } from '../../hooks/playlist/usePlaylistsByItems'

export default function PlaylistsButton({ item, sessionId, asIconButton, size, ...props }) {
  const [t] = useTranslation()
  const user = useUser()
  const { [FLAGS.NEW_PLAYLIST_STATE]: newPlaylistState } = useFlagsContext()
  const managementOverlay = useOverlay()
  const { overlay: loggedOutOverlay } = useLoggedOutOverlay()
  const { getNow } = useTimeTravel()
  const { getConferenceYear } = useConferenceContext()

  const { playlistsByItems, isLoading } = usePlaylistsByItems(newPlaylistState.isOn ? [sessionId] : [])

  // TODO: after new playlist migration, this can be moved to below the loading state and we can get rid of setting the localPlaylistState and just derive it from the response data. With the old state we had to track it locally because we did not update the playlist.items after an item was added.
  const isInSomePlaylist = newPlaylistState.isOn
    ? Boolean(playlistsByItems[sessionId]?.results?.length)
    : Boolean(item?.playlists)
  const [localPlaylistState, setLocalPlaylistState] = useState(isInSomePlaylist)

  // Sync local state when playlists finish loading or isInSomePlaylist changes
  useEffect(() => {
    setLocalPlaylistState(isInSomePlaylist)
  }, [isInSomePlaylist])

  // Only show loading skeleton when the flag is on, user is signed in, and we're actually loading playlists data
  if (newPlaylistState.isOn && user.signedIn && isLoading) {
    return <Skeleton.Button maxWidth={125} width={125} />
  }

  const onClick = (e) => {
    const loggedIn = user?.signedIn ?? false
    if (!loggedIn) {
      trackLink({ name: 'Playlists: logged out', action: 'click_action' })
      loggedOutOverlay?.handleClick?.(e)
    } else {
      trackLink({ name: 'Playlists: logged in', action: 'click_action' })
      managementOverlay.handleClick(e)
    }
  }

  const label = t('playlists.label', 'Playlists')

  // todo: figure out if it's possible to show just the icon in color
  const useColoredButton = false // improvedUxEnabled && localPlaylistState

  const handlePlaylistUpdated = (playlistIds) => {
    if (playlistIds.length > 0) {
      setLocalPlaylistState(true)
    } else {
      setLocalPlaylistState(false)
    }
  }
  const isAvailable = isContentAvailable({ item, now: getNow(), currentConferenceYear: getConferenceYear() })
  return (
    <>
      {isAvailable && (
        <ChewableButton
          asIconButton={asIconButton}
          size={size}
          onClick={onClick}
          Icon={localPlaylistState ? ContentCheck : ContentAdd}
          data-testid="playlists-dialog"
          emphasis="low"
          color={useColoredButton ? 'blue' : null}
          {...props}
        >
          {label}
        </ChewableButton>
      )}
      {(managementOverlay.isOpen || managementOverlay.transitioning) && (
        <PlaylistManagementOverlay
          overlay={managementOverlay}
          sessionId={sessionId}
          onPlaylistUpdated={handlePlaylistUpdated}
        />
      )}
    </>
  )
}
